import React, { useContext } from 'react';
import isEmpty from 'is-empty';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Api from '@/services/Api';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import SeasonsList from '@/components/SeasonsList/SeasonsList';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { Language } from '@types';
import useWhislist from '@/hooks/useWhislist';
import { arrayJoinKeyName, generateCDNUrl } from 'utils';
import { BookmarkIcon, CheckIcon } from '@heroicons/react/outline';
import DialogWishlist from '@/components/Dialog/DialogWishlist';
import { AppContext } from '@/contexts/app.context';
import VideoUnavailablePage from '@/pages/video-unavailable';

type Props = {
  series: any;
  seasons: any;
};

const SeriesPage: ({ series, seasons }: Props) => boolean | JSX.Element = ({
  series,
  seasons,
}: Props) => {
  const { locale } = useRouter();
  const { t } = useTranslation();
  const { isOnWhislist, toggleContent } = useWhislist();
  const { isWishlistModalOpen, onCancelWishlistModal, onAcceptWishlistModal } =
    useContext(AppContext);

  if (isEmpty(series)) {
    return <VideoUnavailablePage />;
  }

  return (
    <>
      <Head>
        <title> {series.film_title} | Butaca</title>
        <meta
          name="description"
          content={
            locale === Language.ES
              ? series.description_es
              : series.description_en
          }
        />
      </Head>
      <div className="container mx-auto px-4 xl:px-0 pb-8">
        <div className="flex flex-col w-full">
          <div className="relative overflow-hidden rounded-lg transition-transform duration-300 ease-in-out h-auto">
            <div className="transition-opacity z-30 duration-300 flex flex-col justify-end absolute top-0 right-0 left-0 bottom-0">
              <div className="flex justify-end bg-gradient-to-t from-black to-transparent py-8" />
            </div>
            <div className={`w-full`}>
              <Image
                src={generateCDNUrl(series.bg?.name || series.poster?.name)}
                width={896}
                height={504}
                layout="responsive"
                alt={series.film_title}
              />
            </div>
          </div>
          <div className="flex-col mt-3 space-y-3 w-full lg:flex">
            <div className="flex justify-between">
              <div className="space-y-1">
                <div className="text-xl">{series.film_title}</div>
                <div className="font-thin flex text-xs md:text-sm w-full items-center space-x-1 divide-x-2 divide-accent divide-opacity-50">
                  <div className="pr-2 w-auto">
                    {Array.isArray(series[`genre_${locale}`])
                      ? series[`genre_${locale}`].join('  ')
                      : series[`genre_${locale}`]}
                  </div>
                  <div className="px-2 w-auto">{series.production_year}</div>
                  <div className="px-2 w-auto">
                    {arrayJoinKeyName(series.countries, `name_${locale}`)}
                  </div>
                </div>
              </div>
              <button
                className="transition-opacity duration-300 flex rounded-xl p-2 lg:px-3 bg-accent items-center space-x-1 font-semibold lg:text-xs opacity-50 hover:opacity-100"
                onClick={(event) => toggleContent(event, series)}
              >
                {!isOnWhislist(series) ? (
                  <>
                    <BookmarkIcon className="w-6" />
                    <span className="hidden lg:block">{t('queue')}</span>
                  </>
                ) : (
                  <>
                    <CheckIcon className="w-6" />
                    <span className="hidden lg:block">{t('queued')}</span>
                  </>
                )}
              </button>
            </div>
            <p className="font-thin leading-5 pt-2 text-xs md:text-sm">
              {series[`description_${locale}`]}
            </p>
            <div className="leading-5 flex items-center space-x-3 text-sm">
              <span className="text-gray-500 font-semibold">
                {t('director')}
              </span>
              <span className="font-thin">{series.director}</span>
            </div>
            <div className="leading-5 items-center space-x-3 text-sm">
              <span className="text-gray-500 font-semibold">
                {t('starring')}
              </span>
              <span className="font-thin">{series.cast}</span>
            </div>
          </div>
        </div>
        <div className="mt-6">
          <SeasonsList seasons={seasons} />
        </div>
      </div>
      <DialogWishlist
        show={isWishlistModalOpen}
        onCancel={onCancelWishlistModal}
        onAccept={onAcceptWishlistModal}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({
  req,
  locale,
  query,
}) => {
  const { series: seriesSlug } = query;
  const series = await Api.get(`v2/series/${seriesSlug}`, req);
  const seasons = series.seasons || [];

  return {
    props: {
      series,
      seasons,
      ...(await serverSideTranslations(locale, ['common', 'profile'])),
    },
  };
};

export default SeriesPage;
